import { Button, DropdownMenu, useClickOutside, useDropdownDirection } from '@consigli/facade';
import { FC, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { getMeetingId, type SupportedItem } from './action-item-types';
import { getStatusBadgeClass } from './action-items-table-utils';
import { StatusDropdownContent } from './status-dropdown-content';
import { useActionItemMutations } from '../hooks/use-action-item-mutations';

interface EditableStatusProps {
  item: SupportedItem;
  meetingId?: string;
  onUpdate?: (updated: SupportedItem) => void;
}

export const EditableStatus: FC<EditableStatusProps> = ({
  item,
  meetingId: propMeetingId,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const resolvedMeetingId = getMeetingId(item, propMeetingId);
  const { updateItem } = useActionItemMutations(resolvedMeetingId);

  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const openDirection = useDropdownDirection({
    containerRef,
    isOpen: showDropdown,
    dropdownHeight: 250, // Header (~40px) + 4 status options (~50px each) = ~240px + buffer
  });

  const handleClickOutside = useCallback(() => {
    setShowDropdown(false);
  }, []);

  useClickOutside({
    ref: containerRef,
    onClickOutside: handleClickOutside,
    enabled: showDropdown,
  });

  const updateStatus = async (newStatus: string) => {
    if (newStatus === item.completionStatus) {
      setShowDropdown(false);
      return;
    }

    try {
      await updateItem(item.id, {
        completionStatus: newStatus,
      });
      onUpdate?.({ ...item, completionStatus: newStatus } as SupportedItem);
      toast.success(t('meeting-secretary.action-items.item-updated'));
    } catch {
      toast.error(t('meeting-secretary.meetings.error-updating-item'));
    }
    setShowDropdown(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <Button
        tertiary
        onClick={(e) => {
          e.stopPropagation();
          setShowDropdown((prev) => !prev);
        }}
        className={`!px-2 !py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(item.completionStatus)} hover:opacity-80 transition-opacity`}
      >
        {t(`meeting-secretary.action-items.status-${item.completionStatus.replace(/_/g, '-')}`)}
      </Button>

      <DropdownMenu
        isOpen={showDropdown}
        direction={openDirection}
        header={t('meeting-secretary.action-items.status', 'Status')}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
      >
        <StatusDropdownContent onSelect={updateStatus} selectedStatus={item.completionStatus} />
      </DropdownMenu>
    </div>
  );
};
